// src/pages/DeliveryFormPage.jsx
import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CartContext } from '../contexts/CartContext';
import { LocationContext } from '../contexts/LocationContext';
import Header from '../components/layout/Header';
import PaymentModal from '../components/PaymentModal';
import { getStorageItem, setStorageItem } from '../utils/storage';
import ApiService from '../services/ApiService';
import '../styles/delivery.css';
import { useAuth } from '../contexts/AuthContext';

const DeliveryFormPage = () => {
  const navigate = useNavigate();
  const { items, quantities, calculateTotal, clearCart } = useContext(CartContext);
  const { userLocation, distributorId, nearestDistributor, deliveryLocation, deliveryAddress } = useContext(LocationContext);
  const { user } = useAuth();
  
  // États du formulaire
  const [deliveryType, setDeliveryType] = useState('self');
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState('weekly');
  const [timeSlot, setTimeSlot] = useState('14-18');
  const [weeklyDeliveryDay, setWeeklyDeliveryDay] = useState(1); // Nouveau: 1 = Lundi par défaut
  // États pour la livraison mensuelle
  const [monthlyPattern, setMonthlyPattern] = useState('relative'); // 'fixed', 'relative', 'business'
  const [monthlyFixedDay, setMonthlyFixedDay] = useState(15); // Pour pattern 'fixed'
  const [monthlyRelativePosition, setMonthlyRelativePosition] = useState('first'); // Pour pattern 'relative'
  const [monthlyRelativeWeekday, setMonthlyRelativeWeekday] = useState(1); // Pour pattern 'relative'
  const [monthlyBusinessDay, setMonthlyBusinessDay] = useState('first_business'); // Pour pattern 'business'
  const [paymentMethod, setPaymentMethod] = useState(''); // Changé à vide par défaut
  const [formValues, setFormValues] = useState({
    firstName: user?.prenom || '',
    phone: user?.telephone || '',
    deliveryAddress: '', // Nouveau champ pour l'adresse de livraison
    latitude: deliveryLocation ? deliveryLocation.latitude : (userLocation ? userLocation.latitude : ''),
    longitude: deliveryLocation ? deliveryLocation.longitude : (userLocation ? userLocation.longitude : ''),
    recipientFirstName: '',
    recipientPhone: '',
    location: ''
  });
  const [deliveryAddressText, setDeliveryAddressText] = useState(deliveryAddress || '');
  const [displayedDeliveryAddress, setDisplayedDeliveryAddress] = useState(''); // Pour l'affichage sous "Lieu de livraison"
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [redirectPath, setRedirectPath] = useState(null);
  // État pour le statut de vérification du téléphone
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [originalPhone, setOriginalPhone] = useState('');
  
  // États pour le modal de paiement
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [orderDataForPayment, setOrderDataForPayment] = useState(null);
  
  // État pour l'affichage des informations validées
  const [validatedInfo, setValidatedInfo] = useState({
    deliveryAddress: '',
    phone: '',
    isValidated: false
  });

  // Jours de la semaine pour le sélecteur
  const weekDays = [
    { value: 0, label: 'Dimanche' },
    { value: 1, label: 'Lundi' },
    { value: 2, label: 'Mardi' },
    { value: 3, label: 'Mercredi' },
    { value: 4, label: 'Jeudi' },
    { value: 5, label: 'Vendredi' },
    { value: 6, label: 'Samedi' }
  ];
  
  // Redirection lorsque redirectPath est défini
  useEffect(() => {
    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    }
  }, [redirectPath, navigate]);
  
  // Charger les données sauvegardées au démarrage
  useEffect(() => {
    // Charger les données utilisateur si disponibles
    if (user) {
      setFormValues(prevValues => ({
        ...prevValues,
        firstName: user.prenom || prevValues.firstName,
        phone: user.telephone || prevValues.phone
      }));
      // Stocker le téléphone original et le statut de vérification
      setOriginalPhone(user.telephone || '');
      // CORRECTION : S'assurer que telephone_verifie est bien un booléen
      setPhoneVerified(user.telephone_verifie === 1 || user.telephone_verifie === true);
      
      // DEBUG : Afficher les valeurs pour comprendre le problème
      console.log('User data:', {
        telephone: user.telephone,
        telephone_verifie: user.telephone_verifie,
        phoneVerified: user.telephone_verifie === 1 || user.telephone_verifie === true
      });
    }
    
    const savedData = getStorageItem('deliveryInfo');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setFormValues(prevValues => ({
          ...prevValues,
          ...parsedData
        }));
        // Si une adresse était sauvegardée, l'afficher
        if (parsedData.deliveryAddress) {
          setDisplayedDeliveryAddress(parsedData.deliveryAddress);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des informations de livraison:', error);
      }
    }
    
    // Charger l'adresse de livraison si disponible
    if (deliveryAddress) {
      setDeliveryAddressText(deliveryAddress);
    }
  }, [deliveryAddress, user]);
  
  // Vérifier le statut du téléphone lorsqu'il change
  useEffect(() => {
    const checkPhoneStatus = async () => {
      if (formValues.phone && formValues.phone.length === 9) {
        try {
          const response = await ApiService.checkPhoneVerificationStatus(formValues.phone);
          if (response.success && response.exists) {
            setPhoneVerified(response.telephone_verifie === true);
            console.log('Phone verification status:', response.telephone_verifie);
          }
        } catch (error) {
          console.error('Erreur lors de la vérification du statut du téléphone:', error);
        }
      }
    };
    
    // Vérifier uniquement si le numéro a changé
    if (formValues.phone !== originalPhone) {
      checkPhoneStatus();
    }
  }, [formValues.phone, originalPhone]);
  
  // Rediriger si le panier est vide
  useEffect(() => {
    if (items.length === 0) {
      navigate('/welcome-page');
    }
  }, [items, navigate]);
  
  // Gestionnaire de changement des inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
    
    // Si le numéro de téléphone change, réinitialiser le statut de vérification
    if (name === 'phone' && value !== originalPhone) {
      setPhoneVerified(false);
    }
    
    // Effacer l'erreur pour ce champ
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };
  
  // Gestionnaire pour le blur du champ adresse
  const handleAddressBlur = () => {
    if (formValues.deliveryAddress.trim()) {
      setDisplayedDeliveryAddress(formValues.deliveryAddress);
    }
  };
  
  // Gestionnaire pour valider les informations
  const handleValidateInfo = () => {
    const phoneRegex = /^6[0-9]{8}$/;
    const errors = {};
    
    // Valider le complément d'adresse
    if (!formValues.deliveryAddress?.trim() || formValues.deliveryAddress.trim().length < 5) {
      errors.deliveryAddress = 'Le complément d\'adresse est requis (minimum 5 caractères)';
    }
    
    // Valider le téléphone
    if (!phoneRegex.test(formValues.phone)) {
      errors.phone = 'Le numéro doit commencer par 6 et avoir 9 chiffres';
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    // Si tout est valide, mettre à jour les informations validées
    setValidatedInfo({
      deliveryAddress: formValues.deliveryAddress,
      phone: formValues.phone,
      isValidated: true
    });
    
    // Effacer les erreurs
    setFormErrors({});
    
    // Sauvegarder dans le localStorage
    const dataToSave = {
      ...formValues,
      deliveryAddress: formValues.deliveryAddress,
      phone: formValues.phone
    };
    setStorageItem('deliveryInfo', JSON.stringify(dataToSave));
  };
  
  // Activer la géolocalisation
  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      setFormErrors({
        ...formErrors,
        coordinates: 'La géolocalisation n\'est pas supportée par votre navigateur'
      });
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        setFormValues({
          ...formValues,
          latitude: lat.toFixed(6),
          longitude: lng.toFixed(6)
        });
        
        // Sauvegarder pour utilisation future
        const savedData = getStorageItem('deliveryInfo');
        const data = savedData ? JSON.parse(savedData) : {};
        data.latitude = lat.toFixed(6);
        data.longitude = lng.toFixed(6);
        setStorageItem('deliveryInfo', JSON.stringify(data));
      },
      (error) => {
        let errorMessage = 'Erreur lors de l\'obtention de votre position';
        
        if (error.code === 1) {
          errorMessage = 'Vous avez refusé l\'accès à votre position';
        } else if (error.code === 2) {
          errorMessage = 'Position non disponible';
        } else if (error.code === 3) {
          errorMessage = 'La demande de position a expiré';
        }
        
        setFormErrors({
          ...formErrors,
          coordinates: errorMessage
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };
  
  // Valider le formulaire
  const validateForm = () => {
    const errors = {};
    const phoneRegex = /^6[0-9]{8}$/;
    
    if (deliveryType === 'self') {
      if (!formValues.firstName?.trim()) {
        errors.firstName = 'Le prénom est requis';
      }
      if (!phoneRegex.test(formValues.phone)) {
        errors.phone = 'Le numéro doit commencer par 6 et avoir 9 chiffres';
      }
      if (!formValues.deliveryAddress?.trim() || formValues.deliveryAddress.trim().length < 5) {
        errors.deliveryAddress = 'Le complément d\'adresse est requis (minimum 5 caractères)';
      }
      if (!formValues.latitude || !formValues.longitude) {
        errors.coordinates = 'Les coordonnées sont requises';
      }
      // Vérifier que les informations ont été validées
      if (!validatedInfo.isValidated) {
        errors.validation = 'Veuillez valider vos informations en cliquant sur le bouton de validation';
      }
    } else { // deliveryType === 'other'
      if (!formValues.firstName?.trim()) {
        errors.firstName = 'Votre prénom est requis';
      }
      if (!formValues.recipientFirstName?.trim()) {
        errors.recipientFirstName = 'Le prénom du destinataire est requis';
      }
      if (!phoneRegex.test(formValues.phone)) {
        errors.phone = 'Votre numéro doit commencer par 6 et avoir 9 chiffres';
      }
      if (!phoneRegex.test(formValues.recipientPhone)) {
        errors.recipientPhone = 'Le numéro du destinataire doit commencer par 6 et avoir 9 chiffres';
      }
      if (!formValues.deliveryAddress?.trim() || formValues.deliveryAddress.trim().length < 5) {
        errors.deliveryAddress = 'Le complément d\'adresse est requis (minimum 5 caractères)';
      }
      // Vérifier qu'on a bien les coordonnées du lieu de livraison
      if (!deliveryLocation || !deliveryLocation.latitude || !deliveryLocation.longitude) {
        errors.location = 'Aucun lieu de livraison sélectionné. Veuillez retourner à l\'accueil.';
      }
    }
    
    // Vérifier qu'un mode de paiement est sélectionné
    if (!paymentMethod) {
      errors.payment = 'Veuillez sélectionner un mode de paiement';
    }
    
    return errors;
  };
  
// === GESTION DU SUCCÈS DU PAIEMENT ===
const handlePaymentSuccess = async (paymentData) => {
  console.log("Payment success - paymentData reçu:", paymentData);

  try {
    // 1. Récupérer les données sauvegardées
    const savedDeliveryInfo = JSON.parse(localStorage.getItem('deliveryInfo'));
    const savedPaymentData = JSON.parse(localStorage.getItem('paymentData'));

    if (!savedDeliveryInfo || !savedPaymentData) {
      throw new Error("Données de commande manquantes");
    }

    // Préparer les données de la commande
    const orderData = {
      montant_total: paymentData.amount,
      id_stock_source: savedPaymentData.id_distributeur,
      statut: 'en_cours',
      mode_paiement: paymentData.operator === 'MTN' ? 'MoMo' : 'OM',
      items: items.map(item => ({
        id_produit: item.id,
        nom: item.displayName,
        quantite: quantities[item.id],
        prix_unitaire: item.price,
        sous_total: item.price * quantities[item.id]
      })),
      contact_livraison_nom: savedDeliveryInfo.deliveryType === 'self' 
        ? savedDeliveryInfo.firstName 
        : savedDeliveryInfo.recipientFirstName,
      contact_livraison_telephone: savedDeliveryInfo.deliveryType === 'self' 
        ? savedDeliveryInfo.phone 
        : savedDeliveryInfo.recipientPhone,
      adresse_livraison: savedDeliveryInfo.deliveryAddress,
      adresse_google_places: savedDeliveryInfo.deliveryAddressText,
      latitude_livraison: savedDeliveryInfo.deliveryType === 'self'
        ? parseFloat(savedDeliveryInfo.latitude)
        : (savedDeliveryInfo.deliveryLocation?.latitude || null),
      longitude_livraison: savedDeliveryInfo.deliveryType === 'self'
        ? parseFloat(savedDeliveryInfo.longitude)
        : (savedDeliveryInfo.deliveryLocation?.longitude || null),
      livraison_recurrente: savedDeliveryInfo.isRecurring,
      frequence_recurrence: savedDeliveryInfo.isRecurring ? savedDeliveryInfo.frequency : null,
      creneau_horaire: savedDeliveryInfo.isRecurring ? savedDeliveryInfo.timeSlot : null,
      jour_livraison_hebdo: (savedDeliveryInfo.isRecurring && savedDeliveryInfo.frequency === 'weekly') 
        ? savedDeliveryInfo.weeklyDeliveryDay : null,
      pattern_livraison_mensuel: (savedDeliveryInfo.isRecurring && savedDeliveryInfo.frequency === 'monthly') 
        ? savedDeliveryInfo.monthlyPattern : null,
      jour_mensuel_fixe: (savedDeliveryInfo.isRecurring && savedDeliveryInfo.frequency === 'monthly' && savedDeliveryInfo.monthlyPattern === 'fixed') 
        ? savedDeliveryInfo.monthlyFixedDay : null,
      position_relative: (savedDeliveryInfo.isRecurring && savedDeliveryInfo.frequency === 'monthly' && savedDeliveryInfo.monthlyPattern === 'relative') 
        ? savedDeliveryInfo.monthlyRelativePosition : null,
      jour_semaine_relatif: (savedDeliveryInfo.isRecurring && savedDeliveryInfo.frequency === 'monthly' && savedDeliveryInfo.monthlyPattern === 'relative') 
        ? savedDeliveryInfo.monthlyRelativeWeekday : null,
      type_jour_ouvrable: (savedDeliveryInfo.isRecurring && savedDeliveryInfo.frequency === 'monthly' && savedDeliveryInfo.monthlyPattern === 'business') 
        ? savedDeliveryInfo.monthlyBusinessDay : null,
      selfDelivery: savedDeliveryInfo.deliveryType === 'self',
      client: {
        prenom: savedDeliveryInfo.firstName,
        telephone: savedDeliveryInfo.phone
      },
      save_address: true,
      address_data: {
        libelle: savedDeliveryInfo.deliveryAddress,
        description: savedDeliveryInfo.deliveryAddress,
        latitude: savedDeliveryInfo.deliveryType === 'self'
          ? parseFloat(savedDeliveryInfo.latitude)
          : (savedDeliveryInfo.deliveryLocation?.latitude || null),
        longitude: savedDeliveryInfo.deliveryType === 'self'
          ? parseFloat(savedDeliveryInfo.longitude)
          : (savedDeliveryInfo.deliveryLocation?.longitude || null)
      }
    };

    // ✅ Créer la commande APRÈS paiement réussi
    const orderResult = await ApiService.createOrder(orderData);
    const orderId = orderResult.id_commande;
    console.log("Commande créée après paiement:", orderResult);
	
	// 3. Mettre à jour la transaction avec id_commande
    await ApiService.linkPaymentToOrder({
      pay_token: paymentData.pay_token,
      reference_id: paymentData.reference_id,
      order_id: orderId
    });

    // Vider le panier
    //clearCart();

    // Nettoyer le localStorage
    localStorage.removeItem('deliveryInfo');
    localStorage.removeItem('paymentData');

    // Rediriger vers la page de commande
    navigate(`/order/${orderResult.id_commande}`, {
      state: { paymentSuccess: true }
    });

  } catch (error) {
    console.error('Erreur création commande post-paiement:', error);
    setSubmitError('Erreur lors de la finalisation de la commande.');
    navigate('/welcome-page');
  }
};
  
  // === SOUMISSION DU FORMULAIRE (sans créer la commande) ===
const handleSubmit = async (e) => {
  e.preventDefault();
  const errors = validateForm();
  if (Object.keys(errors).length > 0) {
    setFormErrors(errors);
    if (errors.payment) setSubmitError(errors.payment);
    return;
  }

  setIsSubmitting(true);
  setSubmitError('');

  try {
    // Sauvegarder TOUTES les données nécessaires pour la création post-paiement
    const deliveryInfoToSave = {
      deliveryType,
      firstName: formValues.firstName,
      phone: formValues.phone,
      deliveryAddress: formValues.deliveryAddress,
      deliveryAddressText,
      latitude: formValues.latitude,
      longitude: formValues.longitude,
      recipientFirstName: formValues.recipientFirstName,
      recipientPhone: formValues.recipientPhone,
      deliveryLocation,
      isRecurring,
      frequency,
      timeSlot,
      weeklyDeliveryDay,
      monthlyPattern,
      monthlyFixedDay,
      monthlyRelativePosition,
      monthlyRelativeWeekday,
      monthlyBusinessDay
    };

    const paymentDataToSave = {
      montant_total: calculateTotal(),
      payment_method: paymentMethod,
      id_distributeur: distributorId || (nearestDistributor ? nearestDistributor.id : 1)
    };

    localStorage.setItem('deliveryInfo', JSON.stringify(deliveryInfoToSave));
    localStorage.setItem('paymentData', JSON.stringify(paymentDataToSave));

    // Ouvrir le modal de paiement avec les données minimales
    setOrderDataForPayment({
      montant_total: calculateTotal(),
      //montant_total: 100,
      payment_method: paymentMethod,
      id_distributeur: distributorId || (nearestDistributor ? nearestDistributor.id : 1),
      phone: formValues.phone
    });

    setShowPaymentModal(true);

  } catch (error) {
    console.error('Erreur préparation paiement:', error);
    setSubmitError('Une erreur est survenue. Veuillez réessayer.');
  } finally {
    setIsSubmitting(false);
  }
};
  
  return (
    <div className="delivery-page">
      <Header 
        title="Livraison" 
        showBackButton={true} 
        onBackClick={() => navigate('/cart')}
      />
      
      <div className="delivery-content fade-in">
        <div className="delivery-section">
          <h2 className="delivery-section-title">Personne à livrer</h2>
          
          <div className="delivery-type-options">
            <button
              type="button"
              onClick={() => setDeliveryType('self')}
              className={`delivery-type-btn ${deliveryType === 'self' ? 'active' : ''}`}
            >
              Moi-même
            </button>
            
            <button
              type="button"
              onClick={() => setDeliveryType('other')}
              className={`delivery-type-btn ${deliveryType === 'other' ? 'active' : ''}`}
            >
              Quelqu'un d'autre
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            {/* Section livraison récurrente */}
            <div className="recurring-section">
              <div className="recurring-header">
                <div>
                  <h3 className="recurring-title">Livraison récurrente</h3>
                  <p className="recurring-subtitle">Faites-vous livrer régulièrement</p>
                </div>
                
                <label className="switch">
                  <input 
                    type="checkbox" 
                    checked={isRecurring}
                    onChange={() => setIsRecurring(!isRecurring)}
                  />
                  <span className="switch-slider"></span>
                </label>
              </div>
              
              {isRecurring && (
                <div className="recurring-options">
                  <div className="form-group">
                    <label className="form-label">À livrer chaque :</label>
                    <div className="frequency-buttons">
                      <button
                        type="button"
                        onClick={() => setFrequency('weekly')}
                        className={`frequency-btn ${frequency === 'weekly' ? 'active' : ''}`}
                      >
                        Semaine
                      </button>
                      
                      <button
                        type="button"
                        onClick={() => setFrequency('monthly')}
                        className={`frequency-btn ${frequency === 'monthly' ? 'active' : ''}`}
                      >
                        Mois
                      </button>
                    </div>
                  </div>
                  
                  {/* Nouveau sélecteur de jour pour livraison hebdomadaire */}
                  {frequency === 'weekly' && (
                    <div className="form-group">
                      <label className="form-label">Jour de livraison</label>
                      <select
                        value={weeklyDeliveryDay}
                        onChange={(e) => setWeeklyDeliveryDay(parseInt(e.target.value))}
                        className="time-slot-select"
                      >
                        {weekDays.map(day => (
                          <option key={day.value} value={day.value}>
                            {day.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Nouveau sélecteur pour livraison mensuelle */}
                  {frequency === 'monthly' && (
                    <>
                      <div className="form-group">
                        <label className="form-label">Type de récurrence mensuelle</label>
                        <div className="monthly-pattern-options">
                          <label className={`pattern-option ${monthlyPattern === 'fixed' ? 'active' : ''}`}>
                            <input
                              type="radio"
                              name="monthlyPattern"
                              value="fixed"
                              checked={monthlyPattern === 'fixed'}
                              onChange={(e) => setMonthlyPattern(e.target.value)}
                              className="pattern-radio"
                            />
                            <div className="pattern-content">
                              <div className="pattern-title">Jour fixe du mois</div>
                              <div className="pattern-description">Ex: Le 15 de chaque mois</div>
                            </div>
                          </label>
                          
                          <label className={`pattern-option ${monthlyPattern === 'relative' ? 'active' : ''}`}>
                            <input
                              type="radio"
                              name="monthlyPattern"
                              value="relative"
                              checked={monthlyPattern === 'relative'}
                              onChange={(e) => setMonthlyPattern(e.target.value)}
                              className="pattern-radio"
                            />
                            <div className="pattern-content">
                              <div className="pattern-title">Position relative dans le mois</div>
                              <div className="pattern-description">Ex: Premier lundi, dernier vendredi</div>
                            </div>
                          </label>
                          
                          <label className={`pattern-option ${monthlyPattern === 'business' ? 'active' : ''}`}>
                            <input
                              type="radio"
                              name="monthlyPattern"
                              value="business"
                              checked={monthlyPattern === 'business'}
                              onChange={(e) => setMonthlyPattern(e.target.value)}
                              className="pattern-radio"
                            />
                            <div className="pattern-content">
                              <div className="pattern-title">Jour ouvrable</div>
                              <div className="pattern-description">Ex: Premier ou dernier jour ouvrable</div>
                            </div>
                          </label>
                        </div>
                      </div>
                      
                      {/* Sous-options selon le pattern sélectionné */}
                      {monthlyPattern === 'fixed' && (
                        <div className="form-group">
                          <label className="form-label">Jour du mois</label>
                          <select
                            value={monthlyFixedDay}
                            onChange={(e) => setMonthlyFixedDay(parseInt(e.target.value))}
                            className="time-slot-select"
                          >
                            {Array.from({length: 28}, (_, i) => i + 1).map(day => (
                              <option key={day} value={day}>
                                {day === 1 ? '1er' : day} du mois
                              </option>
                            ))}
                            <option value="31">Dernier jour du mois</option>
                          </select>
                          <p className="form-hint">Si le jour n'existe pas dans le mois, la livraison sera effectuée le dernier jour du mois</p>
                        </div>
                      )}
                      
                      {monthlyPattern === 'relative' && (
                        <>
                          <div className="form-group">
                            <label className="form-label">Position dans le mois</label>
                            <select
                              value={monthlyRelativePosition}
                              onChange={(e) => setMonthlyRelativePosition(e.target.value)}
                              className="time-slot-select"
                            >
                              <option value="first">Premier</option>
                              <option value="second">Deuxième</option>
                              <option value="third">Troisième</option>
                              <option value="fourth">Quatrième</option>
                              <option value="last">Dernier</option>
                            </select>
                          </div>
                          
                          <div className="form-group">
                            <label className="form-label">Jour de la semaine</label>
                            <div className="weekday-buttons">
                              {weekDays.map(day => (
                                <button
                                  key={day.value}
                                  type="button"
                                  onClick={() => setMonthlyRelativeWeekday(day.value)}
                                  className={`weekday-btn ${monthlyRelativeWeekday === day.value ? 'active' : ''}`}
                                >
                                  {day.label.substring(0, 3)}
                                </button>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                      
                      {monthlyPattern === 'business' && (
                        <div className="form-group">
                          <label className="form-label">Type de jour ouvrable</label>
                          <select
                            value={monthlyBusinessDay}
                            onChange={(e) => setMonthlyBusinessDay(e.target.value)}
                            className="time-slot-select"
                          >
                            <option value="first_business">Premier jour ouvrable du mois</option>
                            <option value="last_business">Dernier jour ouvrable du mois</option>
                          </select>
                        </div>
                      )}
                    </>
                  )}
                  
                  <div className="form-group">
                    <label className="form-label">Créneau horaire préféré</label>
                    <select
                      value={timeSlot}
                      onChange={(e) => setTimeSlot(e.target.value)}
                      className="time-slot-select"
                    >
                      <option value="8-12">8h - 12h</option>
                      <option value="14-18">14h - 18h</option>
                      <option value="18-21">18h - 21h</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
            
            {deliveryType === 'self' ? (
              /* Formulaire pour soi-même */
              <div>
                <div className="form-group">
                  <label className="form-label">Prénom</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formValues.firstName}
                    onChange={handleChange}
                    className={`form-control ${formErrors.firstName ? 'is-invalid' : ''}`}
                    placeholder="Votre prénom"
                  />
                  {formErrors.firstName && (
                    <p className="form-error">{formErrors.firstName}</p>
                  )}
                </div>
                
                <div className="form-group">
                  <label className="form-label">
                    Téléphone
                    {phoneVerified && formValues.phone === originalPhone && (
                      <span className="phone-verified-badge" title="Numéro vérifié">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#4caf50">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                        </svg>
                      </span>
                    )}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formValues.phone}
                    onChange={handleChange}
                    className={`form-control ${formErrors.phone ? 'is-invalid' : ''}`}
                    placeholder="6XXXXXXXX"
                  />
                  <p className="form-hint">Format: 6XXXXXXXX</p>
                  {!phoneVerified && deliveryType === 'self' && (
                    <p className="phone-verification-notice">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#ff9800">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                      </svg>
                      Un SMS de vérification vous sera envoyé si nécessaire
                    </p>
                  )}
                  {formErrors.phone && (
                    <p className="form-error">{formErrors.phone}</p>
                  )}
                </div>
                
                {/* Nouveau champ Complément d'adresse avec bouton de validation */}
                <div className="form-group">
                  <label className="form-label">Complément d'adresse</label>
                  <div className="input-with-button">
                    <input
                      type="text"
                      name="deliveryAddress"
                      value={formValues.deliveryAddress}
                      onChange={handleChange}
                      className={`form-control ${formErrors.deliveryAddress ? 'is-invalid' : ''}`}
                      placeholder="Ex: Carrefour Bastos, Face pharmacie du soleil"
                    />
                    <button
                      type="button"
                      onClick={handleValidateInfo}
                      className={`validate-button ${validatedInfo.isValidated && validatedInfo.deliveryAddress === formValues.deliveryAddress ? 'validated' : ''}`}
                      title="Valider les informations"
                    >
                      {validatedInfo.isValidated && validatedInfo.deliveryAddress === formValues.deliveryAddress ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="form-hint">Minimum 5 caractères</p>
                  {!(validatedInfo.isValidated && validatedInfo.deliveryAddress === formValues.deliveryAddress) && (
                    <p className="validate-instruction">Saisissez votre complément d'adresse et cliquez sur le bouton rouge pour valider</p>
                  )}
                  {formErrors.deliveryAddress && (
                    <p className="form-error">{formErrors.deliveryAddress}</p>
                  )}
                </div>
                
                <div className="form-group">
                  <label className="form-label">Lieu de livraison</label>
                  
                  {/* Champs cachés pour les coordonnées */}
                  <input type="hidden" name="latitude" value={formValues.latitude} />
                  <input type="hidden" name="longitude" value={formValues.longitude} />
                  
                  {formValues.latitude && formValues.longitude ? (
                    <div className="location-success">
                      <div className="location-header">
                        <svg className="w-4 h-4" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                        </svg>
                        <span className="location-title">Adresse complète</span>
                      </div>
                      <div className="location-details">
                        {deliveryAddressText && (
                          <div className="location-item">
                            <span className="location-label">Lieu de livraison:</span>
                            <span className="location-value">{deliveryAddressText}</span>
                          </div>
                        )}
                        {validatedInfo.isValidated && (
                          <>
                            <div className="location-item">
                              <span className="location-label">Complément d'adresse:</span>
                              <span className="location-value">{validatedInfo.deliveryAddress}</span>
                            </div>
                            <div className="location-item">
                              <span className="location-label">Numéro de téléphone:</span>
                              <span className="location-value">{validatedInfo.phone}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleGetLocation}
                      className="get-location-btn"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z" />
                      </svg>
                      Obtenir ma position
                    </button>
                  )}
                  
                  {formErrors.coordinates && (
                    <p className="form-error">{formErrors.coordinates}</p>
                  )}
                  {formErrors.validation && (
                    <p className="form-error">{formErrors.validation}</p>
                  )}
                </div>
              </div>
            ) : (
              /* Formulaire pour quelqu'un d'autre */
              <div>
                <div className="form-group">
                  <label className="form-label">Votre prénom (expéditeur)</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formValues.firstName}
                    onChange={handleChange}
                    className={`form-control ${formErrors.firstName ? 'is-invalid' : ''}`}
                    placeholder="Votre prénom"
                  />
                  {formErrors.firstName && (
                    <p className="form-error">{formErrors.firstName}</p>
                  )}
                </div>
                
                <div className="form-group">
                  <label className="form-label">Votre téléphone (expéditeur)</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formValues.phone}
                    onChange={handleChange}
                    className={`form-control ${formErrors.phone ? 'is-invalid' : ''}`}
                    placeholder="6XXXXXXXX"
                  />
                  <p className="form-hint">Format: 6XXXXXXXX</p>
                  {formErrors.phone && (
                    <p className="form-error">{formErrors.phone}</p>
                  )}
                </div>
                
                <div className="form-group">
                  <label className="form-label">Prénom du destinataire</label>
                  <input
                    type="text"
                    name="recipientFirstName"
                    value={formValues.recipientFirstName}
                    onChange={handleChange}
                    className={`form-control ${formErrors.recipientFirstName ? 'is-invalid' : ''}`}
                    placeholder="Prénom du destinataire"
                  />
                  {formErrors.recipientFirstName && (
                    <p className="form-error">{formErrors.recipientFirstName}</p>
                  )}
                </div>
                
                <div className="form-group">
                  <label className="form-label">Téléphone du destinataire</label>
                  <input
                    type="tel"
                    name="recipientPhone"
                    value={formValues.recipientPhone}
                    onChange={handleChange}
                    className={`form-control ${formErrors.recipientPhone ? 'is-invalid' : ''}`}
                    placeholder="6XXXXXXXX"
                  />
                  <p className="form-hint">Format: 6XXXXXXXX</p>
                  {formErrors.recipientPhone && (
                    <p className="form-error">{formErrors.recipientPhone}</p>
                  )}
                </div>
                
                {/* Nouveau champ Adresse de livraison */}
                <div className="form-group">
                  <label className="form-label">Complément d'adresse</label>
                  <div className="input-with-button">
                    <input
                      type="text"
                      name="deliveryAddress"
                      value={formValues.deliveryAddress}
                      onChange={handleChange}
                      className={`form-control ${formErrors.deliveryAddress ? 'is-invalid' : ''}`}
                      placeholder="Ex: Carrefour Bastos, Face pharmacie du soleil"
                    />
                    <button
                      type="button"
                      onClick={handleValidateInfo}
                      className={`validate-button ${validatedInfo.isValidated && validatedInfo.deliveryAddress === formValues.deliveryAddress ? 'validated' : ''}`}
                      title="Valider les informations"
                    >
                      {validatedInfo.isValidated && validatedInfo.deliveryAddress === formValues.deliveryAddress ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="form-hint">Minimum 5 caractères</p>
                  {!(validatedInfo.isValidated && validatedInfo.deliveryAddress === formValues.deliveryAddress) && (
                    <p className="validate-instruction">Saisissez votre complément d'adresse et cliquez sur le bouton rouge pour valider</p>
                  )}
                  {formErrors.deliveryAddress && (
                    <p className="form-error">{formErrors.deliveryAddress}</p>
                  )}
                </div>
                
                {/* Afficher le lieu de livraison sélectionné */}
                <div className="form-group">
                  <label className="form-label">Lieu de livraison</label>
                  {deliveryAddressText || displayedDeliveryAddress ? (
                    <div className="location-info">
                      <svg className="location-icon-small" width="16" height="36" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                      </svg>
                      <span>{displayedDeliveryAddress || deliveryAddressText}</span>
                    </div>
                  ) : (
                    <p className="form-error">Aucun lieu de livraison sélectionné. Veuillez retourner à l'accueil.</p>
                  )}
                </div>
              </div>
            )}
            
            {/* Section Mode de paiement */}
			<div className="payment-section">
			  <h3 className="payment-section-title">Mode de paiement</h3>
			  
			  <div className="payment-options">
				{/* Option Mobile Money - À GAUCHE */}
				<label className={`payment-option ${paymentMethod === 'MoMo' ? 'active' : ''}`}>
				  <input
					type="radio"
					name="paymentMethod"
					value="MoMo"
					checked={paymentMethod === 'MoMo'}
					onChange={(e) => setPaymentMethod(e.target.value)}
					className="payment-radio"
				  />
				  <div className="payment-option-content">
					<div className="payment-option-icon">
					  <img src="/images/payment/mtn-momo-logo.png" alt="Mobile Money" className="payment-logo momo-logo" />
					</div>
					<div className="payment-option-text">
					  <span className="payment-option-title">Mobile Money</span>
					</div>
				  </div>
				</label>

				{/* Option Orange Money - À DROITE */}
				<label className={`payment-option ${paymentMethod === 'OM' ? 'active' : ''}`}>
				  <input
					type="radio"
					name="paymentMethod"
					value="OM"
					checked={paymentMethod === 'OM'}
					onChange={(e) => setPaymentMethod(e.target.value)}
					className="payment-radio"
				  />
				  <div className="payment-option-content">
					<div className="payment-option-icon">
					  <img src="/images/payment/orange-money-logo.png" alt="Orange Money" className="payment-logo om-logo" />
					</div>
					<div className="payment-option-text">
					  <span className="payment-option-title">Orange Money</span>
					</div>
				  </div>
				</label>
			  </div>
			  
			  {formErrors.payment && (
				<p className="form-error" style={{ marginTop: '0.5rem' }}>{formErrors.payment}</p>
			  )}
			</div>
            
            {/* Message d'erreur global */}
            {submitError && (
              <div className="form-error-message">
                {submitError}
              </div>
            )}
            
            {/* Bouton de soumission */}
            <button
              type="submit"
              disabled={isSubmitting || !paymentMethod}
              className="submit-button"
            >
              {isSubmitting ? (
                <>
                  <div className="spinner"></div>
                  Validation en cours...
                </>
              ) : (
                <>
                  Continuer vers le paiement
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z" />
                  </svg>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
      
      {/* Modal de paiement */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => {
			setShowPaymentModal(false);
			setIsSubmitting(false);
		}}
        orderData={orderDataForPayment}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default DeliveryFormPage;